import { useState } from 'react'
import { Button, HStack, Heading, Text, VStack } from '@chakra-ui/react'
import { FiEye, FiPlus, FiTrash2, FiUserCheck, FiUserMinus } from 'react-icons/fi'
import { toaster } from '@/components/ui/toaster'
import { ResponsiveTable, StatusChip, type ResponsiveColumn, type RowAction } from '../../../shared'
import { useCallBatches, useDeleteCallBatch, useUnassignCallBatch } from '../../../../hooks/useFollowUpCalls'
import { extractApiMessage } from '../../../../lib/apiError'
import { shortDayLabel } from '../routes/routeDates'
import { AssignCallBatchSheet } from './AssignCallBatchSheet'
import { CallBatchDetail } from './CallBatchDetail'
import { CreateCallBatchModal } from './CreateCallBatchModal'
import type { FollowUpCallBatchDto, FollowUpKind } from '../../../../types/followUp'

export interface CallBatchesPanelProps {
  campaignId: string
  kind: FollowUpKind
  customKindId?: string | null
  /** Shown in the new-list title, e.g. "Call-back". */
  kindLabel?: string
  /** Hides "New call list" (CF-1 P5 Chase: the section's own prepare → confirm → hand-off is the only
   *  way to make a chase call list). Listing, assign, view and delete stay. */
  hideCreate?: boolean
}

/** A call-mode Follow-up section's call lists: make one, assign it to a canvasser, see progress. */
export function CallBatchesPanel({
  campaignId, kind, customKindId = null, kindLabel, hideCreate = false,
}: CallBatchesPanelProps) {
  const { data, isLoading, error } = useCallBatches(campaignId, {
    kind,
    ...(customKindId ? { customKindId } : {}),
  })
  const { mutate: unassign, isPending: isUnassigning } = useUnassignCallBatch(campaignId)
  const { mutate: deleteBatch, isPending: isDeleting } = useDeleteCallBatch(campaignId)

  const [creating, setCreating] = useState(false)
  const [assigning, setAssigning] = useState<FollowUpCallBatchDto | null>(null)
  const [viewingId, setViewingId] = useState<string | null>(null)

  const batches = data?.items ?? []

  const fail = (title: string) => (err: unknown) =>
    toaster.create({ title, description: extractApiMessage(err, 'Please try again.'), type: 'error', duration: 7000 })

  const handleDelete = (b: FollowUpCallBatchDto) => {
    if (!window.confirm('Delete this call list? People on it go back to the open list.')) return
    deleteBatch(b.id, { onError: fail('Could not delete the call list') })
  }

  const columns: ResponsiveColumn<FollowUpCallBatchDto>[] = [
    { header: 'Call list', mobileRole: 'title', cell: b => <Text as="span">{b.label}</Text> },
    {
      header: 'Assigned to',
      mobileRole: 'meta',
      cell: b => (
        <Text as="span">
          {b.assignedCanvasserName ?? 'Unassigned'}
          {b.scheduledDate ? ` · ${shortDayLabel(b.scheduledDate)}` : ''}
        </Text>
      ),
    },
    {
      header: 'Progress',
      mobileRole: 'badge',
      cell: b => (
        <StatusChip variant={b.total > 0 && b.called >= b.total ? 'success' : 'neutral'}>
          {b.called}/{b.total} called
        </StatusChip>
      ),
    },
  ]

  const rowActions: RowAction<FollowUpCallBatchDto>[] = [
    { label: 'View', icon: FiEye, onClick: b => setViewingId(b.id) },
    { label: 'Assign', icon: FiUserCheck, onClick: b => setAssigning(b) },
    {
      label: 'Unassign',
      icon: FiUserMinus,
      hidden: b => !b.assignedGrantId,
      isLoading: () => isUnassigning,
      onClick: b => unassign(b.id, { onError: fail('Could not unassign the call list') }),
    },
    { label: 'Delete', icon: FiTrash2, destructive: true, isLoading: () => isDeleting, onClick: handleDelete },
  ]

  return (
    <VStack align="stretch" gap={3} w="full" minW={0}>
      <HStack justify="space-between" gap={2} flexWrap="wrap">
        <Heading size="sm">Call lists</Heading>
        {!hideCreate && (
          <Button size="sm" colorPalette="brand" minH="44px" onClick={() => setCreating(true)}>
            <FiPlus />New call list
          </Button>
        )}
      </HStack>
      {!!error && !data ? (
        <Text fontSize="sm" color="danger.fg">{extractApiMessage(error, 'Could not load call lists.')}</Text>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={batches}
          keyFn={b => b.id}
          rowActions={rowActions}
          onRowClick={b => setViewingId(b.id)}
          isLoading={isLoading}
          emptyMessage={hideCreate ? 'No call lists yet.' : 'No call lists yet. Make one from the open people below.'}
        />
      )}
      {creating && !hideCreate && (
        <CreateCallBatchModal
          isOpen
          onClose={() => setCreating(false)}
          campaignId={campaignId}
          kind={kind}
          customKindId={customKindId}
          kindLabel={kindLabel}
        />
      )}
      <AssignCallBatchSheet
        isOpen={!!assigning}
        onClose={() => setAssigning(null)}
        campaignId={campaignId}
        batch={assigning}
      />
      <CallBatchDetail
        isOpen={!!viewingId}
        onClose={() => setViewingId(null)}
        campaignId={campaignId}
        batchId={viewingId}
      />
    </VStack>
  )
}
